import { useContext } from 'react';

import { LayerContext } from 'state/LayerContext';
import { useGetSegmentInfoQuery } from 'state/apiSlice';
import { useAppSelector } from 'state/hooks';
import {
  selectComparison,
  selectComparisonEndDate,
  selectCurrentProjectInfo,
  selectSegmentId,
  selectShortestAvailableBin,
  selectTargetDate,
  selectTargetEndDate,
} from 'state/workflowSlice';

import {
  formatTargetSeriesName,
  formatTypicalSeriesName,
} from 'features/chart/nrtChartCommon';
import { formatTime, formatTimeOfDay } from 'features/common/datetimeUtils';
import {
  ExportType,
  nrtCommonInfo,
  triggerCsvExport,
  useExportFilename,
} from 'features/common/exportUtils';
import { useRouteInfo } from 'features/common/hooks/useRouteInfo';
import { ts_to_minutes } from 'features/common/slotsUtils';
import { formatDistance } from 'features/common/utils';
import {
  formatPercentage,
  get_total_info,
} from 'features/workflow_queuing/QueueChartTooltipContents';
import { get_timestamp } from 'features/workflow_queuing/common';
import { useQueueDataProvider } from 'features/workflow_queuing/queueDataProvider';

export function useQueueExportData(skip?: boolean) {
  const { layer } = useContext(LayerContext);
  const segmentId = useAppSelector(selectSegmentId);
  const comparison = useAppSelector(selectComparison);
  const targetDate = useAppSelector(selectTargetDate);
  const targetEndDate = useAppSelector(selectTargetEndDate);
  const userProject = useAppSelector(selectCurrentProjectInfo);
  const binSize = useAppSelector(selectShortestAvailableBin);

  const filename = useExportFilename(ExportType.Data);
  const { currentData: queue_data } = useQueueDataProvider(skip);
  const { currentData: segmentData } = useGetSegmentInfoQuery(
    { layerId: layer, routeIds: [segmentId] },
    { skip: !layer || !segmentId || skip },
  );

  const { feature: routeFeature, savedRoute } = useRouteInfo(skip);

  const segment = segmentData && segmentData?.segments[segmentId];
  const handleExport = () => {
    if (skip) return;
    const data = queue_data.segment_times.features;
    const typicalSeriesName = formatTypicalSeriesName(
      comparison,
      targetDate,
      userProject.uses_middle_east_day_types,
      false,
      undefined,
      true,
    );
    const targetSeriesName = formatTargetSeriesName(
      targetDate,
      false,
      targetEndDate,
    );
    const header = [
      'Time Start',
      'Time End',
      'Route Queued Length',
      'Percent Queued',
      `${targetSeriesName} Traveltime (HH:MM:SS)`,
      `${typicalSeriesName} Traveltime (HH:MM:SS)`,
      `${targetSeriesName} Delay`,
    ];

    const csv = [
      `Name, ${savedRoute ? savedRoute.routeName : segment.properties.name}`,
      `ID, ${segmentId}`,
      ...nrtCommonInfo(
        targetDate,
        comparison,
        segment,
        userProject.uses_middle_east_day_types,
        targetEndDate,
      ),
      `Route/Segment length, ${formatDistance(
        routeFeature.properties.route_length,
        userProject.uses_metric,
      )}`,
      '',
      '',
      header.join(', '),
    ];

    const timestamps = {};
    for (let i = 0; i < data.length; i++) {
      const ts = get_timestamp(data[i]);
      timestamps[ts] = timestamps[ts] || [];
      timestamps[ts].push(data[i]);
    }

    const all_timestamps = Object.keys(timestamps).sort();

    for (let i = 0; i < all_timestamps.length; i += 1) {
      const time = all_timestamps[i];
      const mins = ts_to_minutes(time.replace('Z', ''), targetDate);
      const info = get_total_info(timestamps[time] as any);
      if (mins >= 0 && mins < 24 * 60) {
        const row = [
          formatTimeOfDay(mins),
          formatTimeOfDay(mins + binSize),
          formatDistance(1000 * info.queued_length_km, userProject.uses_metric),
          formatPercentage(info.queued_length_km / info.total_length_km),
          formatTime(60 * (info.total_delay_min + info.typical_traveltime_min)),
          formatTime(60 * info.typical_traveltime_min),
          formatTime(60 * info.total_delay_min),
        ];
        csv.push(row.join(','));
      }
    }
    triggerCsvExport(filename, csv);
  };

  const isActive = !skip && !!queue_data;

  return { handleExport, isActive };
}
